/**
 * Money digits: grouped thousands, always 2 decimals, follows the company
 * digit-style setting.
 *
 * Before this lane: `toLocaleString('en-US', …)` pinned Latin digits
 * deliberately, because an unpinned `toLocaleString()` follows the UI
 * language and ٩٬٢٥٠٫٠٠ inside a dir="ltr" money span was not what the
 * workshop reads on an invoice.
 *
 * After (owner decision 2026-10-10): the choice is per-company and lives on
 * `companies.settings.number_digits` ∈ {'latin', 'arabic'}. Latin is the
 * default for new AND existing companies, so the behaviour for the typical
 * tenant is unchanged — but a tenant that opts into 'arabic' now sees
 * ١٬٩٣٢٫٠٠ for its money lines.
 *
 * WHICH LANGUAGE DECIDES THE DIGITS (round 5): the language of the LINE the
 * figure sits on. `lang` carries it explicitly where the caller knows it —
 * 'ar' for a value printed once for both languages on a bilingual public
 * document (it follows the tenant's setting), 'en' for an English-only line
 * (Latin whatever the tenant picked — `Discount (١٬٢٣٤٫٥٠) applies only…`
 * was exactly the bug). When the caller does NOT know the line language
 * (a staff `<Money>` with no lang prop, a bare `fmtMoney()` call), the
 * CURRENT UI LANGUAGE decides: an English screen prints Latin, an Arabic
 * screen follows the company setting. Before round 5 the undefined case
 * always asked for the Arabic locale, so an English-UI screen under an
 * 'arabic' tenant printed ١٬٢٣٤٫٥٠ next to Latin dates.
 *
 * The UI language comes from uiLanguage() (utils/locale) — the i18next
 * singleton src/i18n.js configures, read WITHOUT importing src/i18n.js,
 * which would crash every suite that mocks react-i18next.
 *
 * When the effective style is Latin the value formats through 'en-US'
 * exactly as the pre-lane helper did — grouping, decimal mark, and the
 * `NaN` a non-number degrades to are byte-identical to main. Only the
 * Arabic-Indic path asks Intl for the Arabic locale.
 *
 * NOT FOR ZATCA XML/QR — those need machine format (Latin digits) whatever
 * the setting; see backend/services/zatca/.
 */
import { appLocale, getNumberDigits, uiLanguage } from './locale';

const LATIN_LOCALE = 'en-US';

/**
 * The language of the line: an explicit `lang` wins ('ar' vs anything
 * else); an absent one falls back to the current UI language.
 */
const lineLang = (lang) => {
  if (lang === 'ar') return 'ar';
  if (lang) return 'en';
  return uiLanguage();
};

/**
 * @param {number|string} value
 * @param {string} [lang]  the language of the LINE the figure sits on.
 *   Undefined → the current UI language decides (see the header). 'ar' →
 *   the company's digit setting decides (a value printed once for both
 *   languages follows the tenant's choice). Any other value ('en', …) →
 *   Latin en-US, byte-identical to the pre-lane format: an English-only
 *   line never carries Arabic-Indic digits, whatever the tenant picked.
 */
export const fmtMoney = (value, lang) => {
  const locale =
    lineLang(lang) === 'ar' && getNumberDigits() === 'arabic' ? appLocale('ar') : LATIN_LOCALE;
  let out = Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Strip the bidi markers Intl sneaks around '-' under 'ar' locales (LRM
  // U+200E, RLM U+200F and ALM U+061C — the last one IS the Arabic-script
  // mark, so the strip must cover it too, not just the Latin-side ones), so
  // a string comparison against the plain form still holds (matters for
  // tests, and for any caller that compares against a literal).
  out = out.replace(/[\u200E\u200F\u061C]/g, '');
  // A negative that rounds away — -0.001, or a literal -0 — formats as
  // "-0.00" (Latin) or the Arabic-script counterpart (U+061C before "-٠٫٠٠"),
  // which on a financial screen reads as a credit that is not there. Each
  // script normalises to its OWN zero.
  if (/^[\s]*-0\.00[\s]*$/.test(out)) {
    out = '0.00';
  } else if (/^[\s]*-٠٫٠٠[\s]*$/.test(out)) {
    out = '٠٫٠٠';
  }
  return out;
};

export default fmtMoney;
